import { get } from "svelte/store"
import { apiRequest, obj2str } from "../../api/hooks/lib/ssr"
import { apiBaseOverride, login, newNotification } from "./lib/store"
import { postLogin } from "./lib/functions/CommerceAPIs/bigCommerce/customer"

export const api = async <T>(
    endpoint: string,
    options?: ApiOptions,
    body?: any
): Promise<{ data: T; count: number } | any> => {
    const _apiBaseOverride = get(apiBaseOverride) || ""
    if (!options.headers) options.headers = {}
    if (options.method) options.method = options.method.toUpperCase()
    if (options.useJwt) options.headers.Authorization = `Bearer ${options.jwtToUse || (await getLogin())?.tokenString}`
    let data = await apiRequest(_apiBaseOverride + endpoint, options, body, fetch)

    return data
}
let refreshingToken: Promise<Login> | null
export const getLogin = async (signal?: AbortSignal): Promise<Login | null> => {
    let _l = get(login)
    if ((_l?.tokenData?.exp || 0) - 60 < new Date().getTime() / 1000) {
        // token expired, relogin via refresh cookie, run only one in parallel
        if (!refreshingToken)
            refreshingToken = postLogin({
                email: null,
                password: null,
            })

        try {
            _l = await refreshingToken
        } finally {
            refreshingToken = null
        }
    }

    return _l
}
// init login state
getLogin().catch((e) => {
    console.log("getting login via refresh cookie failed:", e)
})
const cache: {
    [key: string]: {
        expire: number
        data: any
    }
} = {}

type EntryTypeSwitch<T> = T extends "medialib"
    ? MedialibEntry
    : T extends "content"
    ? ContentEntry
    : T extends "navigation"
    ? NavigationEntry
    : T extends "tag"
    ? TagEntry
    : T extends "dummyCartEndpoint"
    ? DummyCartEndpoint
    : T extends "productBenefit"
    ? productBenefit
    : never

export async function getDBEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string
): Promise<EntryTypeSwitch<T>[]> {
    const c = await api<EntryTypeSwitch<T>[]>(collectionName, { filter, sort, limit, offset, projection })
    return c.data
}

export async function getCachedEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string
): Promise<EntryTypeSwitch<T>[]> {
    const filterStr = obj2str({ collectionName, filter, sort, limit, offset, projection })
    if (cache[filterStr] && cache[filterStr].expire >= Date.now()) {
        return cache[filterStr].data
    }
    const entries = await getDBEntries<T>(collectionName, filter, sort, limit, offset, projection)
    const inOneHour = Date.now() + 1000 * 60 * 60
    cache[filterStr] = { expire: inOneHour, data: entries }
    return entries
}

export async function getDBEntry<T extends CollectionName>(collectionName: T, filter: { [key: string]: any }) {
    return (await getDBEntries<T>(collectionName, filter, "_id"))?.[0]
}

export async function getCachedEntry<T extends CollectionName>(collectionName: T, filter: { [key: string]: any }) {
    return (await getCachedEntries<T>(collectionName, filter, "_id"))?.[0]
}

export async function postDBEntry<T extends CollectionName>(collectionName: T, entry: EntryTypeSwitch<T>) {
    return api<EntryTypeSwitch<T>>(collectionName, { method: "POST" }, entry)
}
